import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import SignupBanner from './SignupBanner';
import UpInput from '../../components/Common/UpInput';
import UpButton from '../../components/Common/UpButton';
import UpLoadingCover from '../../components/Common/UpLoadingCover';
import {
  submitSignupContact,
  getSignupErrorMessage,
  getSignupRequestId,
} from '../../api/signup';

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const MAX_ADDITIONAL_EMAILS = 4;

// "EUR 49 per location per month, invoiced monthly, Net 30" — invoice
// merchants only, and only when the API sends the price (older APIs don't).
function getInvoicePriceLine(state) {
  if (
    state?.payment_at_signup !== 'invoice' ||
    !state?.currency ||
    state?.price === undefined ||
    state?.price === null ||
    state?.price === ''
  ) {
    return null;
  }

  const terms =
    state?.frequency === 'annual_prepaid'
      ? 'per location per year, invoiced annually in advance'
      : 'per location per month, invoiced monthly, Net 30';
  return `${state.currency} ${state.price} ${terms}`;
}

// The entry card: "confirm this is you". The contact here is the POC —
// every signup email goes to this address (never the billing contact, which
// the next screen collects separately for invoices).
function EntryScreen({ token, state, onStateChange, onStart }) {
  const [editing, setEditing] = useState(false);
  const [brandName, setBrandName] = useState(state?.business || '');
  const [contactName, setContactName] = useState(
    [state?.first_name, state?.last_name].filter(Boolean).join(' '),
  );
  const [email, setEmail] = useState(state?.email || '');
  const [additionalEmails, setAdditionalEmails] = useState(
    state?.additional_emails || [],
  );
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const priceLine = getInvoicePriceLine(state);

  const { isLoading: loading, mutate: saveAndStart } = useMutation({
    mutationFn: params => submitSignupContact(token, params),
    onSuccess: newState => {
      onStateChange(newState);
      onStart();
    },
    onError: setSubmitError,
  });

  function isDirty() {
    const storedName = [state?.first_name, state?.last_name]
      .filter(Boolean)
      .join(' ');
    const storedAdditional = state?.additional_emails || [];
    return (
      brandName.trim() !== (state?.business || '') ||
      contactName.trim() !== storedName ||
      email.trim() !== (state?.email || '') ||
      additionalEmails.map(e => e.trim()).filter(Boolean).join(',') !==
        storedAdditional.join(',')
    );
  }

  function validate() {
    const errors = {};

    if (!brandName.trim()) {
      errors.brandName = 'Please enter your brand name';
    }

    if (!contactName.trim()) {
      errors.contactName = 'Please enter the contact name';
    }

    // Same shape + length the server enforces — fail fast, never truncate.
    if (!EMAIL_REGEX.test(email.trim()) || email.trim().length > 80) {
      errors.email = 'Please enter a valid email address';
    }

    additionalEmails.forEach((extra, index) => {
      const value = extra.trim();
      if (value && (!EMAIL_REGEX.test(value) || value.length > 80)) {
        errors[`additional_${index}`] = 'Please enter a valid email address';
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleContinue() {
    setSubmitError(null);

    if (loading) {
      return;
    }

    if (!editing || !isDirty()) {
      onStart();
      return;
    }

    if (!validate()) {
      return;
    }

    saveAndStart({
      business: brandName.trim(),
      contact_name: contactName.trim(),
      email: email.trim(),
      additional_emails: additionalEmails.map(e => e.trim()).filter(Boolean),
    });
  }

  function onFieldChange(field, setter) {
    return value => {
      setSubmitError(null);
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
      setter(value);
    };
  }

  function setAdditionalEmail(index, value) {
    setSubmitError(null);
    setFieldErrors(prev => ({ ...prev, [`additional_${index}`]: '' }));
    setAdditionalEmails(prev => prev.map((e, i) => (i === index ? value : e)));
  }

  function removeAdditionalEmail(index) {
    setFieldErrors({});
    setAdditionalEmails(prev => prev.filter((_, i) => i !== index));
  }

  return (
    <div className="signup-entry">
      <div className="entry-left">
        <SignupBanner />
      </div>
      <div className="entry-card-wrap">
        <div className="entry-card relative">
          <UpLoadingCover loading={loading} />
          <div className="signup-column">
            <h1 className="signup-title">
              Let&apos;s get {state?.business || 'your brand'} live on
              UrbanPiper
            </h1>
            <p className="signup-sub">
              Confirm this is you, then complete signup — it takes about 5
              minutes.
            </p>
            {(state?.partner_name || priceLine) && (
              <div className="entry-deal">
                {state?.partner_name && (
                  <div>
                    Referred by{' '}
                    <span className="font-semibold text-up-clr-black">
                      {state.partner_name}
                    </span>
                  </div>
                )}
                {priceLine && <div>{priceLine}</div>}
              </div>
            )}
            {!editing && (
              <>
                <div className="entry-summary">
                  <div className="entry-row">
                    <span className="entry-row-label">Brand</span>
                    <span className="entry-row-value">{state?.business}</span>
                  </div>
                  <div className="entry-row">
                    <span className="entry-row-label">Contact name</span>
                    <span className="entry-row-value">
                      {[state?.first_name, state?.last_name]
                        .filter(Boolean)
                        .join(' ')}
                    </span>
                  </div>
                  <div className="entry-row">
                    <span className="entry-row-label">Contact email</span>
                    <span className="entry-row-value">{state?.email}</span>
                  </div>
                  {(state?.additional_emails || []).map(extra => (
                    <div className="entry-row" key={extra}>
                      <span className="entry-row-label">Also emailed</span>
                      <span className="entry-row-value">{extra}</span>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="text-sm text-up-blue underline -mt-4 mb-6 block"
                  onClick={() => setEditing(true)}>
                  Edit these details or add more emails
                </button>
              </>
            )}
            {editing && (
              <div className="mb-8">
                <UpInput
                  autoFocus
                  type="text"
                  name="business"
                  label="Brand"
                  placeholder="The brand you run on UrbanPiper"
                  value={brandName}
                  onChange={onFieldChange('brandName', setBrandName)}
                  errorContent={fieldErrors.brandName}
                />
                <UpInput
                  type="text"
                  name="contact_name"
                  wrapperClass="mt-5"
                  label="Contact name"
                  placeholder="Ex: Jordan Smith"
                  value={contactName}
                  onChange={onFieldChange('contactName', setContactName)}
                  errorContent={fieldErrors.contactName}
                />
                <UpInput
                  type="text"
                  name="email"
                  wrapperClass="mt-5"
                  label="Contact email"
                  placeholder="Ex: jordan@example.com"
                  helpContent="Your signup and portal emails go here."
                  value={email}
                  onChange={onFieldChange('email', setEmail)}
                  errorContent={fieldErrors.email}
                />
                {additionalEmails.map((extra, index) => (
                  /* rows are positional — an index key keeps focus while typing */
                  /* eslint-disable-next-line react/no-array-index-key */
                  <div className="flex items-start gap-2" key={index}>
                    <UpInput
                      type="text"
                      name={`additional_email_${index}`}
                      wrapperClass="mt-5 flex-1"
                      label={index === 0 ? 'Also email' : ''}
                      placeholder="Ex: owner@example.com"
                      value={extra}
                      onChange={value => setAdditionalEmail(index, value)}
                      errorContent={fieldErrors[`additional_${index}`]}
                    />
                    <button
                      type="button"
                      aria-label="Remove email"
                      className="text-up-clr-dark-grey self-center px-1"
                      onClick={() => removeAdditionalEmail(index)}>
                      ✕
                    </button>
                  </div>
                ))}
                {additionalEmails.length < MAX_ADDITIONAL_EMAILS && (
                  <button
                    type="button"
                    className="text-sm text-up-blue underline mt-4"
                    onClick={() => setAdditionalEmails(prev => [...prev, ''])}>
                    + Add another email
                  </button>
                )}
              </div>
            )}
            {submitError && (
              <div className="text-sm text-up-clr-semantic-red break-words mb-6">
                {getSignupErrorMessage(submitError)}
                {getSignupRequestId(submitError) && (
                  <span className="block text-xs text-up-clr-light-grey mt-1">
                    Reference: {getSignupRequestId(submitError)}
                  </span>
                )}
              </div>
            )}
            <UpButton disabled={loading} onClick={handleContinue}>
              {loading ? 'Saving…' : 'Complete signup'}
            </UpButton>
            <p className="signup-footnote">
              You can complete this signup within 30 days of receiving the
              referral email.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EntryScreen;
